// Responsible for configuring and firing Snowplow / RTB analytics events.
// Mirrors dispute-cm-UI/src/core/utilities/AnalyticsUtil.js (RTB analytics portion),
// adapted to read its configuration from the React app's runtime config object.
//
// Expected config shape (see public/cpi_config.json -> analytics):
//   {
//     enabled: boolean,
//     trackerUrl: 'spm.apps.gov.bc.ca'
//   }

import { startPlowing } from './rtb_analytics.js';

let analyticsConfig = null;
let analyticsFailedToLoad = false;
let analyticsInitialized = false;

const isAnalyticsEnabled = () =>
  !analyticsFailedToLoad &&
  !!analyticsConfig?.enabled &&
  !!analyticsConfig?.trackerUrl;

// Install the Snowplow bootstrap loader and start the tracker (page view, activity,
// and link-click tracking). Called from the App entry point after config is loaded.
// No-op unless config.enabled && config.trackerUrl are set. Runs at most once so
// React StrictMode double-rendering / hot reloads can't queue duplicate trackers.
const initializeAnalyticsTracking = (config = {}) => {
  if (analyticsInitialized) return;

  analyticsConfig = config;
  if (!isAnalyticsEnabled()) return;

  analyticsInitialized = true;
  try {
    startPlowing.call(window, analyticsConfig.trackerUrl);
  } catch (err) {
    analyticsFailedToLoad = true;
  }
};

const trackEvent = (eventData = {}) => {
  if (!isAnalyticsEnabled()) return;
  try {
    window.snowplow('trackSelfDescribingEvent', {
      schema: 'iglu:ca.bc.gov.rtb/click/jsonschema/1-0-0',
      data: eventData,
    });
  } catch (err) {
    console.warn('[Error] Unable to log rtb analytics event', eventData);
  }
};

// Pass in a url to track navigation to.
// Always uses current url as the source.
const trackUrlClickEvent = (targetUrl) => {
  trackEvent({
    source_url: window.location.href,
    target_url: targetUrl,
  });
};

const Analytics = {
  initializeAnalyticsTracking,
  trackUrlClickEvent,
};

export default Analytics;